import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api/context';
import { Chip } from '../design/primitives/Misc';
import { Popover } from '../design/primitives/Overlay';

// Tags editor: chips + a text input; suggestions from /v1/tags (q=), Enter/comma adds,
// Backspace on empty removes the last. Value is string[] (tag names).

export function TagsInput({
  value,
  onChange,
  disabled,
  placeholder = 'Add tag…',
  id,
  'aria-label': ariaLabel,
  suggest = true,
}: {
  value: string[] | null | undefined;
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  suggest?: boolean;
}) {
  const api = useApi();
  const tags = value ?? [];
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sugg = useQuery({
    queryKey: ['tags', 'suggest', text],
    enabled: suggest && open,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        const page = await api.resource<{ id: string; name: string }>('tags').list({ q: text || undefined, limit: 10 });
        return page.data.map((t) => t.name).filter((n) => !tags.includes(n));
      } catch {
        return [] as string[];
      }
    },
  });
  const options = sugg.data ?? [];
  useEffect(() => setActive(0), [text]);

  const add = (t: string) => {
    const name = t.trim();
    if (!name || tags.includes(name)) return;
    onChange([...tags, name]);
    setText('');
  };
  const remove = (t: string) => onChange(tags.filter((x) => x !== t));

  return (
    <div className="hf-tags" ref={wrapRef} onClick={() => inputRef.current?.focus()}>
      {tags.map((t) => (
        <Chip key={t} onRemove={disabled ? undefined : () => remove(t)}>
          {t}
        </Chip>
      ))}
      <input
        ref={inputRef}
        id={id}
        className="hf-tags-input"
        aria-label={ariaLabel ?? 'Tags'}
        placeholder={tags.length ? '' : placeholder}
        value={text}
        disabled={disabled}
        role="combobox"
        aria-expanded={open && options.length > 0}
        aria-autocomplete="list"
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(open && options[active] && text ? options[active]! : text);
          } else if (e.key === 'Backspace' && !text && tags.length) {
            remove(tags[tags.length - 1]!);
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, options.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          }
        }}
      />
      <Popover open={open && options.length > 0} onClose={() => setOpen(false)} anchor={wrapRef} matchWidth role="presentation">
        <div role="listbox" className="hf-listbox">
          {options.map((o, i) => (
            <div key={o} role="option" aria-selected={i === active} className={['hf-option', i === active && 'active'].filter(Boolean).join(' ')} onMouseDown={(e) => e.preventDefault()} onClick={() => add(o)}>
              {o}
            </div>
          ))}
        </div>
      </Popover>
    </div>
  );
}
